/**
 * Token Service
 *
 * Manages JWT tokens in memory with proactive refresh scheduling.
 * Tokens are stored in JavaScript memory ONLY (not localStorage/sessionStorage).
 * This means tokens are lost on page refresh - which is the intended security behavior.
 */

import { log } from '../utils/DebugLogger'
import { calculateTokenFreshness, isTokenExpired, isTokenExpiringSoon } from '../utils/jwt'

export type TokenStatus = 'EXPIRED' | 'EXPIRING_SOON' | 'FRESH' | 'INVALID'

export type RefreshCallback = () => Promise<void>

export interface RefreshedTokens {
  accessToken: string
  idToken: string
}

/**
 * The tokens inside a /v1/user/refreshToken answer. The auth service answers it
 * bare ({ accessToken, idToken }), unlike login's { success, dados } envelope;
 * both are accepted so no caller logs the user out over the shape (DS 2026-0070).
 */
export function tokensFromRefresh(body: unknown): RefreshedTokens | null {
  if (!body || typeof body !== 'object') return null
  const b = body as { accessToken?: unknown; idToken?: unknown; success?: unknown; dados?: { accessToken?: unknown; idToken?: unknown } }
  const fonte = b.success === true && b.dados ? b.dados : b
  return typeof fonte.accessToken === 'string' && fonte.accessToken && typeof fonte.idToken === 'string' && fonte.idToken
    ? { accessToken: fonte.accessToken, idToken: fonte.idToken }
    : null
}

export interface TokenDebugInfo {
  hasTokens: boolean
  status: TokenStatus
  freshness?: string
  schedulerActive?: boolean
}

class TokenService {
  private accessToken: string | null = null
  private idToken: string | null = null
  private refreshTimer: ReturnType<typeof setInterval> | null = null
  private refreshCallback: RefreshCallback | null = null

  setTokens(accessToken: string, idToken: string): void {
    log('TokenService', 'setTokens', '✅ Storing tokens in memory')
    this.accessToken = accessToken
    this.idToken = idToken
  }

  getAccessToken(): string | null {
    return this.accessToken
  }

  getIdToken(): string | null {
    return this.idToken
  }

  hasTokens(): boolean {
    return !!(this.accessToken && this.idToken)
  }

  clearTokens(): void {
    log('TokenService', 'clearTokens', '🗑️ Clearing all tokens from memory')
    this.accessToken = null
    this.idToken = null
    this.stopRefreshScheduler()
  }

  getTokenStatus(token: string | null = null): TokenStatus {
    const tokenToCheck = token || this.accessToken

    if (!tokenToCheck) {
      return 'INVALID'
    }

    if (isTokenExpired(tokenToCheck)) {
      return 'EXPIRED'
    }

    if (isTokenExpiringSoon(tokenToCheck)) {
      return 'EXPIRING_SOON'
    }

    return 'FRESH'
  }

  startRefreshScheduler(refreshCallback: RefreshCallback): void {
    if (!refreshCallback || typeof refreshCallback !== 'function') {
      log('TokenService', 'startRefreshScheduler', '❌ Invalid refresh callback')
      return
    }

    log('TokenService', 'startRefreshScheduler', '⏰ Starting refresh scheduler (check every 60s)')
    this.refreshCallback = refreshCallback

    this.stopRefreshScheduler()

    this._checkAndRefresh()

    this.refreshTimer = setInterval(() => {
      this._checkAndRefresh()
    }, 60000)
  }

  stopRefreshScheduler(): void {
    if (this.refreshTimer) {
      log('TokenService', 'stopRefreshScheduler', '⏹️ Stopping refresh scheduler')
      clearInterval(this.refreshTimer)
      this.refreshTimer = null
    }
  }

  private async _checkAndRefresh(): Promise<void> {
    const token = this.getAccessToken()

    if (!token) {
      log('TokenService', '_checkAndRefresh', '⚠️ No token to check')
      return
    }

    const status = this.getTokenStatus(token)
    const freshness = calculateTokenFreshness(token)

    log('TokenService', '_checkAndRefresh', `📊 Token status: ${status}, freshness: ${freshness.toFixed(1)}%`)

    if (status === 'EXPIRING_SOON' && this.refreshCallback) {
      log('TokenService', '_checkAndRefresh', '🔄 Token expiring soon - triggering proactive refresh')
      try {
        await this.refreshCallback()
        log('TokenService', '_checkAndRefresh', '✅ Proactive refresh completed')
      } catch (error) {
        log('TokenService', '_checkAndRefresh', '❌ Refresh failed:', error)
      }
    } else if (status === 'EXPIRED') {
      log('TokenService', '_checkAndRefresh', '❌ Token expired - stopping scheduler')
      this.stopRefreshScheduler()
    } else if (status === 'FRESH') {
      log('TokenService', '_checkAndRefresh', '✅ Token still fresh')
    }
  }

  getDebugInfo(): TokenDebugInfo {
    const token = this.getAccessToken()

    if (!token) {
      return {
        hasTokens: false,
        status: 'INVALID'
      }
    }

    return {
      hasTokens: this.hasTokens(),
      status: this.getTokenStatus(token),
      freshness: `${calculateTokenFreshness(token).toFixed(1)}%`,
      schedulerActive: !!this.refreshTimer
    }
  }
}

export const tokenService = new TokenService()
